import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingUp, DollarSign, Percent, Clock, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';

const defaultIndicators = {
  aguaFria: {
    porcentajeAceptacion: 0,
    valorPesos: 0,
    consumoEsperado: 0
  },
  aguaCaliente: {
    porcentajeAceptacion: 0,
    valorPesos: 0,
    consumoEsperado: 0
  },
  gas: {
    porcentajeAceptacion: 0,
    valorPesos: 0,
    consumoEsperado: 0
  },
  electricidad: {
    porcentajeAceptacion: 0,
    valorPesos: 0,
    consumoEsperado: 0
  },
  detergentes: {
    porcentajeAceptacion: 0,
    valorPesos: 0,
    consumoEsperado: 0
  },
  manoDeObra: {
    porcentajeAceptacion: 0,
    valorPesos: 0,
    consumoEsperado: 0
  },
  metasOperativas: {
    porcentajeRechazoMax: 0,
    kgPorHoraAutorizadaMin: 0,
    pctCiclosAbortadosMax: 0
  },
  controlProduccion: {
    factorCarga: 90,
    toleranciaVariacion: 5,
    toleranciaClienteAlistado: 5
  },
  costoKilo: 0,
  isCostoKilo: false,
  costoPorHora: 0
};

const ConfigureIndicatorsModal = ({ isOpen, onClose, onSave, laundry }) => {
  const [indicators, setIndicators] = useState(defaultIndicators);
 
  useEffect(() => {
    if (laundry?.indicadores) {
      setIndicators(prev => ({
        ...defaultIndicators,
        ...laundry.indicadores
      }));
    } else {
      setIndicators(defaultIndicators);
    }
  }, [laundry]);

  const handleChange = (category, field, value) => {
    const numericValue = parseFloat(value);
    const normalizedValue = Number.isFinite(numericValue) ? parseFloat(numericValue.toFixed(3)) : 0;
    setIndicators(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: normalizedValue
      }
    }));
  };

  const handleMetaOperativaChange = (field, value) => {

    const numericValue = parseFloat(value);

    const normalizedValue = Number.isFinite(numericValue) ? parseFloat(numericValue.toFixed(3)) : 0;

    setIndicators(prev => ({

      ...prev,

      metasOperativas: {

        ...defaultIndicators.metasOperativas,

        ...(prev.metasOperativas || {}),

        [field]: normalizedValue

      }

    }));

  };

  const handleControlProduccionChange = (field, value) => {
    const numericValue = parseFloat(value);
    const normalizedValue = Number.isFinite(numericValue) ? parseFloat(numericValue.toFixed(3)) : 0;
    setIndicators(prev => ({
      ...prev,
      controlProduccion: {
        ...defaultIndicators.controlProduccion,
        ...(prev.controlProduccion || {}),
        [field]: normalizedValue
      }
    }));
  };

  const handleCostoKiloChange = (value) => {
    setIndicators(prev => ({
      ...prev,
      costoKilo: parseFloat(value) || 0
    }));
  };

  const handleCostoKiloToggle = (value) => {
    setIndicators(prev => ({
      ...prev,
      isCostoKilo: Boolean(value)
    }));
  };

  const handleCostoPorHoraChange = (value) => {
    setIndicators(prev => ({
      ...prev,
      costoPorHora: parseFloat(value) || 0
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(indicators);
  };

  if (!isOpen) return null;

  const indicatorConfig = [
    {
      key: 'aguaFria',
      label: 'Agua Fría',
      icon: TrendingUp,
      color: 'blue'
    },
    {
      key: 'aguaCaliente',
      label: 'Agua Caliente',
      icon: TrendingUp,
      color: 'red'
    },
    {
      key: 'gas',
      label: 'Gas',
      icon: TrendingUp,
      color: 'orange'
    },
    {
      key: 'electricidad',
      label: 'Electricidad',
      icon: TrendingUp,
      color: 'yellow'
    },
    {
      key: 'detergentes',
      label: 'Detergentes de Lavado',
      icon: DollarSign,
      color: 'amber'
    },
    {
      key: 'manoDeObra',
      label: 'Mano de Obra',
      icon: TrendingUp,
      color: 'green'
    }
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Configurar Indicadores
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {laundry?.nombre} - Indicadores de Costo por Kilogramo
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              <div className="space-y-6">
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900/30 border border-dashed border-gray-200 dark:border-gray-800 p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="isCostoKilo"
                      checked={indicators.isCostoKilo}
                      onCheckedChange={handleCostoKiloToggle}
                    />
                    <div>
                      <Label htmlFor="isCostoKilo" className="text-base font-semibold flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Costo por kilogramo activo
                      </Label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Marca esta casilla cuando el centro cobre por kilogramo y registra el valor en pesos.
                      </p>
                    </div>
                  </div>
                  {indicators.isCostoKilo && (
                    <div className="space-y-1 pt-2">
                      <Label htmlFor="costoKilo" className="text-sm font-medium">
                        Valor del kilo (COP)
                      </Label>
                      <Input
                        id="costoKilo"
                        type="number"
                        step="1"
                        min="0"
                        value={indicators.costoKilo}
                        onChange={(e) => handleCostoKiloChange(e.target.value)}
                        placeholder="0"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500">
                        Este valor solo se pide cuando el centro opera bajo costo por kilo.
                      </p>
                    </div>
                  )}
                </div>
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900/30 border border-dashed border-gray-200 dark:border-gray-800 p-4 space-y-3">
                  <div>
                    <Label htmlFor="costoPorHora" className="text-base font-semibold flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Costo por Hora de Mano de Obra (COP)
                    </Label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Este valor se usa para calcular el costo de mano de obra por kilogramo en los reportes.
                    </p>
                  </div>
                  <div className="space-y-1 pt-2">
                    <Input
                      id="costoPorHora"
                      type="number"
                      step="1"
                      min="0"
                      value={indicators.costoPorHora}
                      onChange={(e) => handleCostoPorHoraChange(e.target.value)}
                      placeholder="Ej: 10000"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500">
                      Valor en pesos colombianos por hora trabajada. Se multiplica por las horas autorizadas para obtener el costo total de mano de obra.
                    </p>
                  </div>
                </div>

                {/* Metas operativas: las evalúa el Centro de Monitoreo. 0 = sin evaluar. */}
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900/30 border border-dashed border-gray-200 dark:border-gray-800 p-4 space-y-3">
                  <div>
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      Metas Operativas
                    </Label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Umbrales que el Centro de Monitoreo compara contra los resultados reales del centro.
                      Un valor en 0 significa "sin meta" y no genera alertas.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                    <div>
                      <Label htmlFor="meta-porcentajeRechazoMax" className="text-sm font-medium">% máximo de rechazo</Label>
                      <Input
                        id="meta-porcentajeRechazoMax"
                        type="number"
                        step="0.01"
                        min="0"
                        value={indicators.metasOperativas?.porcentajeRechazoMax ?? 0}
                        onChange={(e) => handleMetaOperativaChange('porcentajeRechazoMax', e.target.value)}
                        placeholder="0.00"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Porcentaje de kilos de rechazo sobre kilos procesados que se considera aceptable. Deja 0 para no evaluar.</p>
                    </div>
                    <div>
                      <Label htmlFor="meta-kgPorHoraAutorizadaMin" className="text-sm font-medium">Kg por hora autorizada (mínimo)</Label>
                      <Input
                        id="meta-kgPorHoraAutorizadaMin"
                        type="number"
                        step="0.01"
                        min="0"
                        value={indicators.metasOperativas?.kgPorHoraAutorizadaMin ?? 0}
                        onChange={(e) => handleMetaOperativaChange('kgPorHoraAutorizadaMin', e.target.value)}
                        placeholder="0.00"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Productividad mínima esperada. Si se deja en 0 se usa la Eficiencia Meta de Mano de Obra.</p>
                    </div>
                    <div>
                      <Label htmlFor="meta-pctCiclosAbortadosMax" className="text-sm font-medium">% máximo de ciclos abortados</Label>
                      <Input
                        id="meta-pctCiclosAbortadosMax"
                        type="number"
                        step="0.01"
                        min="0"
                        value={indicators.metasOperativas?.pctCiclosAbortadosMax ?? 0}
                        onChange={(e) => handleMetaOperativaChange('pctCiclosAbortadosMax', e.target.value)}
                        placeholder="0.00"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Sobre el total de ciclos de lavado y secado del período. Deja 0 para no evaluar.</p>
                    </div>
                  </div>
                </div>

                {/* Parámetros del reporte "Control diario de producción" (Reporte 360 v2) */}
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900/30 border border-dashed border-gray-200 dark:border-gray-800 p-4 space-y-3">
                  <div>
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <Percent className="h-4 w-4" />
                      Control diario de producción
                    </Label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Parámetros que usa el reporte "Control diario de producción" para calcular kilos facturables y marcar variaciones.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                    <div>
                      <Label htmlFor="cp-factorCarga" className="text-sm font-medium">Factor de carga (%)</Label>
                      <Input
                        id="cp-factorCarga"
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={indicators.controlProduccion?.factorCarga ?? 90}
                        onChange={(e) => handleControlProduccionChange('factorCarga', e.target.value)}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Kg facturables = kg lavados por ciclos × este %. Es lo que realmente se cobra.</p>
                    </div>
                    <div>
                      <Label htmlFor="cp-toleranciaVariacion" className="text-sm font-medium">Tolerancia de variación (%)</Label>
                      <Input
                        id="cp-toleranciaVariacion"
                        type="number"
                        step="0.1"
                        min="0"
                        value={indicators.controlProduccion?.toleranciaVariacion ?? 5}
                        onChange={(e) => handleControlProduccionChange('toleranciaVariacion', e.target.value)}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Las variaciones que superen este valor se marcan en color.</p>
                    </div>
                    <div>
                      <Label htmlFor="cp-toleranciaClienteAlistado" className="text-sm font-medium">Tolerancia del cliente sobre alistado (%)</Label>
                      <Input
                        id="cp-toleranciaClienteAlistado"
                        type="number"
                        step="0.1"
                        min="0"
                        value={indicators.controlProduccion?.toleranciaClienteAlistado ?? 5}
                        onChange={(e) => handleControlProduccionChange('toleranciaClienteAlistado', e.target.value)}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">El cliente acepta facturar hasta el alistado + este %. Por encima: "SOBRE TOLERANCIA".</p>
                    </div>
                  </div>
                </div>

                {indicatorConfig.map(({ key, label, icon: Icon, color }) => (
                  <Card key={key} className={`border-${color}-200 dark:border-${color}-800`}>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`p-2 rounded-lg bg-${color}-100 dark:bg-${color}-900/30`}>
                          <Icon className={`h-5 w-5 text-${color}-600`} />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {label}
                        </h3>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor={`${key}-porcentaje`} className="flex items-center gap-2">
                            <Percent className="h-4 w-4" />
                            Porcentaje de Aceptación (%)
                          </Label>
                          <Input
                            id={`${key}-porcentaje`}
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={indicators[key].porcentajeAceptacion}
                            onChange={(e) => handleChange(key, 'porcentajeAceptacion', e.target.value)}
                            placeholder="0.00"
                            className="mt-1"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Margen de desviación permitido
                          </p>
                        </div>

                        <div>
                          <Label htmlFor={`${key}-valor`} className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4" />
                            Valor Meta en Pesos (COP/kg)
                          </Label>
                          <Input
                            id={`${key}-valor`}
                            type="number"
                            step="1"
                            min="0"
                            value={indicators[key].valorPesos}
                            onChange={(e) => handleChange(key, 'valorPesos', e.target.value)}
                            placeholder="0"
                            className="mt-1"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Costo objetivo por kilogramo
                          </p>
                        </div>

                        <div>
                          <Label htmlFor={`${key}-consumo`} className="flex items-center gap-2">
                            <TrendingUp className="h-4 w-4" />
                            {key === 'manoDeObra' ? 'Eficiencia Meta (kg/hora)' : key === 'detergentes' ? 'Consumo Meta (mg/kg)' : key === 'gas' ? 'Consumo Meta (m³/kg)' : key === 'electricidad' ? 'Consumo Meta (kWh/kg)' : 'Consumo Meta (L/kg)'}
                          </Label>
                          <Input
                            id={`${key}-consumo`}
                            type="number"
                            step="0.001"
                            min="0"
                            value={indicators[key].consumoEsperado}
                            onChange={(e) => handleChange(key, 'consumoEsperado', e.target.value)}
                            placeholder="0.000"
                            className="mt-1"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {key === 'manoDeObra' ? 'Kg procesados por hora' : key === 'detergentes' ? 'Miligramos por kilogramo' : key === 'gas' ? 'Metros cúbicos por kilogramo' : key === 'electricidad' ? 'Kilovatios-hora por kilogramo' : 'Litros por kilogramo'}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <strong>Meta de Costo:</strong> {indicators[key].valorPesos.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}/kg
                          {indicators[key].porcentajeAceptacion > 0 && (
                            <>
                              {' '}± {indicators[key].porcentajeAceptacion}%
                              {' '}(Rango: {(indicators[key].valorPesos * (1 - indicators[key].porcentajeAceptacion / 100)).toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })} - {(indicators[key].valorPesos * (1 + indicators[key].porcentajeAceptacion / 100)).toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })})
                            </>
                          )}
                        </p>
                        {indicators[key].consumoEsperado > 0 && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            <strong>Meta de {key === 'manoDeObra' ? 'Eficiencia' : 'Consumo'}:</strong> {indicators[key].consumoEsperado.toFixed(3)} {key === 'manoDeObra' ? 'kg/hora' : key === 'detergentes' ? 'mg/kg' : key === 'gas' ? 'm³/kg' : key === 'electricidad' ? 'kWh/kg' : 'L/kg'}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="mt-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Nota:</strong> Los indicadores configurados se utilizarán en los reportes para comparar 
                  la medición real con la meta establecida y calcular la desviación automáticamente.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button type="submit">
                Guardar Indicadores
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ConfigureIndicatorsModal;
